import React, { useMemo } from "react";
import { Loader2, X } from "lucide-react";
import { GenericObjectInterface } from "@/src/utilities";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAppSelector } from "@/src/store/hooks";
import { renderStars } from "../RenderStar";
import { ImageLoader } from "../ImageLoader";
import ComparisonModalContent from "./ComparisonModalContent";

/**
 * @description This component is used to display a comparison table for the selected products
 * @param {string[]} productsSelectedForComparison - Array of document IDs selected for comparison
 * @param {GenericObjectInterface[]} comparedProducts - Array of compared products
 * @param {Function} handleCompare - Function to handle comparison refer to SoftwareReviewSection
 * @returns {React.FC}
 */
function ComparisonTable({
  productsSelectedForComparison,
  comparedProducts,
  handleCompare,
}: {
  productsSelectedForComparison: string[];
  comparedProducts: GenericObjectInterface[];
  handleCompare: (productId: GenericObjectInterface) => void;
}) {
  const { selectedSubcategories, compareProductsLoading, selectedCategories } =
    useAppSelector((root) => root.unGatedContentStateReducer.comparedProducts);
  const { productCategories } = useAppSelector(
    (root) => root.unGatedContentStateReducer
  );

  /**
   * @description This function is used to extract features from the selected products
   * @returns {string[]} Array of features
   */
  const features = useMemo(() => {
    try {
      //Select features from the selected products and remove duplicates by documentId
      return Array.from(
        new Set(
          (productsSelectedForComparison || []).flatMap((productId) => {
            const product = comparedProducts?.find(
              (el) => productId === el?.documentId
            );

            if (!product?.product_feature_values) return [];

            return product.product_feature_values
              .map((fv: any) => fv?.feature_id?.name ?? null) // <-- safe access
              .filter((name: string | null): name is string => Boolean(name)); // keep only real strings
          })
        )
      );
    } catch (error) {
      console.error("Error while extracting features:", error);
      return [];
    }
  }, [productsSelectedForComparison, comparedProducts]);

  /**
   * @description This function is used to get the selected subcategory name
   * @returns {string} Selected subcategory name
   */
  const selectedSubCategoryName = useMemo(() => {
    const selectedCategory = productCategories?.find(
      (category: GenericObjectInterface) =>
        category?.documentId == selectedCategories
    );
    const selectedSubcategoryName =
      selectedCategory?.subcategories?.find(
        (sub: GenericObjectInterface) =>
          sub?.documentId == selectedSubcategories
      );
    return selectedSubcategoryName?.name;
  }, [productCategories, selectedSubcategories, selectedCategories]);

  /**
   * @description This function is used to check if the comparison products are loading
   * @returns {boolean} True if the comparison products are loading
   */
  if (compareProductsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b">
          <th className="text-left p-2 font-semibold">Features</th>
          {productsSelectedForComparison?.map((productId) => {
            const product =
              comparedProducts?.find((el) => productId == el?.documentId) || {};

            return (
              <th key={productId} className="text-center p-4 min-w-[200px] relative">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-2 text-sm">
                    <div className="h-16 w-16 flex items-center justify-center flex-shrink-0">
                      {product?.product_logo_url && (
                        <ImageLoader
                          src={product?.product_logo_url}
                          alt=""
                          width={64}
                          height={64}
                          className="object-contain max-h-full max-w-full"
                        />
                      )}
                    </div>
                    <div className="font-semibold">{product?.name}</div>
                    <div className="flex items-center gap-1">
                      {renderStars(product?.average_stars)}
                    </div>
                    <p className="text-xs truncate w-32">
                      {product?.price_range || 0}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCompare(product)}
                    className="absolute top-4 right-1/4 text-xs font-normal bg-red-100 rounded-full h-6 w-6 flex flex-col items-center justify-center text-muted-foreground cursor-pointer"
                  >
                    <X size={14} className="text-red-700" />
                  </button>
                </div>
              </th>
            );
          })}

          {productsSelectedForComparison?.length < 3 && (
            <th>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Add Software</Button>
                </DialogTrigger>
                <DialogContent className="min-w-full sm:min-w-[90vw] md:min-w-[80vw] lg:min-w-[70vw] max-h-[80vh] overflow-y-auto bg-white">
                  <DialogHeader>
                    <DialogTitle>
                      Select technologies from {selectedSubCategoryName} to compare
                    </DialogTitle>
                  </DialogHeader>
                  <ComparisonModalContent
                    selectedSubcategories={selectedSubcategories || ""}
                    handleCompare={handleCompare}
                  />
                </DialogContent>
              </Dialog>
            </th>
          )}
        </tr>
      </thead>
      <tbody className="overflow-x-hidden">
        {features &&
          features.length > 0 &&
          features.map((feature) => (
            <tr key={feature} className="border-b hover:bg-muted/50">
              <td className="p-4 text-sm font-medium capitalize min-w-[150px]">{feature}</td>
              {productsSelectedForComparison.map((productId) => {
                const product = comparedProducts?.find(
                  (el) => productId === el?.documentId
                );
                const featureValue =
                  product?.product_feature_values.find(
                    (fv: GenericObjectInterface) =>
                      fv?.feature_id?.name === feature
                  )?.feature_value ?? "";

                return (
                  <td
                    key={productId}
                    className="text-left mx-auto text-sm font-medium"
                  >
                    {featureValue ? (
                      <span className="text-sm capitalize px-14">
                        {featureValue}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground px-14">
                        —
                      </span> // if product doesn’t have this feature
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

        {/* {comparisonView === "detailed" && (
      <>
        <tr className="border-b hover:bg-muted/50">
          <td className="p-4 font-medium">Integrations</td>
          {productsSelectedForComparison.map((productId) => {
            const product = comparedProducts?.find(
              (el) => productId === el?.id
            );
            return (
              <td key={productId} className="text-center p-4">
                {product?.integrations}+
              </td>
            );
          })}
        </tr>
        <tr className="border-b hover:bg-muted/50">
          <td className="p-4 font-medium">Security</td>
          {productsSelectedForComparison.map((productId) => {
            const product = comparedProducts?.find(
              (el) => productId === el?.id
            );
            return (
              <td key={productId} className="text-center p-4">
                {product?.security}
              </td>
            );
          })}
        </tr>
        <tr className="border-b hover:bg-muted/50">
          <td className="p-4 font-medium">Support</td>
          {productsSelectedForComparison.map((productId) => {
            const product =
              comparisonData[
                productId as keyof typeof comparisonData
              ];
            return (
              <td
                key={productId}
                className="text-center p-4 text-sm"
              >
                {product.support}
              </td>
            );
          })}
        </tr>
      </>
    )} */}
      </tbody>
    </table>
  );
}

export default ComparisonTable;
